import axios from "axios";

import { BASE_URL } from "../../utils/constants";

/**
 * Password reset (forgot password) API — 3-step, all unauthenticated.
 *
 *   1. POST /users/forgot-password/   { email }                       -> emails a 6-digit code
 *   2. POST /users/verify-reset-otp/  { code }                        -> { token } (valid 15 min)
 *   3. POST /users/password-reset/    { token, password, password2 }  -> { message }
 *
 * Step 2 verifies the OTP on its own and returns a short-lived reset token; the
 * OTP is NOT consumed (re-entering it returns a fresh token). Step 3 takes that
 * token (in the body, not a header) instead of the raw code.
 *
 * All calls are intentionally unauthenticated, so they use the bare `axios`
 * import (not the shared `api` instance, which attaches the auth token).
 */

export const requestPasswordResetCode = (email) =>
  axios.post(`${BASE_URL}/users/forgot-password/`, { email });

export const verifyResetOtp = (code) =>
  axios.post(`${BASE_URL}/users/verify-reset-otp/`, { code });

export const resetPassword = ({ token, password, password2 }) =>
  axios.post(`${BASE_URL}/users/password-reset/`, {
    token,
    password,
    password2,
  });

/**
 * Classify a password-reset (Step 3) failure so the UI can react:
 *   - "expired"  -> reset token past its 15-min TTL
 *   - "used"     -> token already consumed
 *   - "invalid"  -> bad token, or anything else token-related
 *
 * Step 3 now fails with a token error shaped like { token: "invalid_or_expired_token" }
 * (the live backend can't distinguish invalid vs expired, so that combined value
 * lands in the "invalid" bucket). We scan every plausible message field and match
 * on substrings (case-insensitive) so QA can tune phrases without touching the
 * screens. Any recognized kind routes to the dedicated /reset-code-error screen
 * ("start over"); unrecognized failures fall back to a toast.
 */
export const classifyResetError = (error) => {
  const data = error?.response?.data;

  const candidates = [];
  if (typeof data === "string") candidates.push(data);
  if (data && typeof data === "object") {
    ["message", "error", "detail"].forEach((k) => {
      if (typeof data[k] === "string") candidates.push(data[k]);
    });
    // field-level errors: { code: ["..."] } or { errors: { code: ["..."] } }
    const fieldBags = [data, data.errors].filter(
      (b) => b && typeof b === "object"
    );
    fieldBags.forEach((bag) => {
      Object.values(bag).forEach((v) => {
        if (typeof v === "string") candidates.push(v);
        else if (Array.isArray(v)) v.forEach((m) => candidates.push(String(m)));
      });
    });
  }

  const text = candidates.join(" ").toLowerCase();
  if (!text) return null;

  if (text.includes("used") || text.includes("already")) return "used";
  // The live backend returns "invalid_or_expired_code" for both a wrong code
  // and an expired one — it can't distinguish them — so the combined bucket
  // (contains "invalid") maps to "invalid" (honest combined copy). A future
  // distinct "expired" signal (without "invalid") still maps to "expired".
  if (text.includes("invalid")) return "invalid";
  if (text.includes("expire")) return "expired";
  return null;
};
